import React from 'react'
import { modal } from '@codeleap/modals'
import { WebInputFile } from '@codeleap/types'
import { ActivityIndicator, Button, View } from '@/components'
import { useCropPicker, UseCropPicker } from './hook'
import { variantProvider } from '@/app'
import type { ReactCropProps } from 'react-image-crop'

const ReactCrop: (props: ReactCropProps) => JSX.Element = require('react-image-crop').Component

import 'react-image-crop/dist/ReactCrop.css'

type NativeCropProps = Omit<ReactCropProps, 'crop' | 'onChange' | 'onComplete' | 'style'>

export type CropPickerModalProps = Omit<UseCropPicker, 'file' | 'onCrop'> & {
  file: WebInputFile
  reactCrop?: NativeCropProps
}

const CropPickerModal = modal<CropPickerModalProps, { file: WebInputFile | null }>().content(props => {
  const { file, request, aspect, minHeight, minWidth, reactCrop } = props

  const {
    containerStyles,
    imageStyles,
    handleCropChange,
    handleConfirmCrop,
    handleCancelCrop,
    setRelativeCrop,
    crop,
    image,
  } = useCropPicker({
    file: file?.file,
    onCrop: croppedFile => request.resolve({ file: croppedFile }),
    aspect: aspect ?? reactCrop?.aspect,
    minHeight,
    minWidth,
  })

  if (!image?.src) {
    return (
      <View variants={['fullWidth', 'center']}>
        <ActivityIndicator debugName='' />
      </View>
    )
  }

  return (
    <>
      <ReactCrop
        disabled={false}
        {...reactCrop}
        crop={crop}
        onChange={handleCropChange}
        onComplete={(_, relCrop) => setRelativeCrop(relCrop)}
        style={containerStyles}
        aspect={aspect}
      >
        <img src={image?.src} css={[imageStyles]} />
      </ReactCrop>

      <View variants={['fullWidth', 'row', 'marginTop:2', 'gap:2']}>
        <Button text={'Confirm'} onPress={handleConfirmCrop} debugName={'cropModal:confirm'} variants={['fullWidth']} />
        <Button
          text={'Cancel'}
          onPress={handleCancelCrop}
          debugName={'cropModal:confirm'}
          variants={['fullWidth', 'outline']}
        />
      </View>
    </>
  )
})

const styles = variantProvider.createComponentStyle(
  theme => ({
    box: {
      padding: theme.spacing.value(4),
      width: 'calc(80vw - 72px)',
      maxWidth: 900,

      [theme.media.down('medium')]: {
        maxWidth: 400,
      },

      [theme.media.down('small')]: {
        padding: theme.spacing.value(2),
      },
    },
  }),
  true,
)

CropPickerModal.props({ variants: ['centered'], showClose: false, styles: { box: styles.box }})

export { CropPickerModal }
